import { format, isToday, isYesterday } from "date-fns";
import type { TFunction } from "i18next";
import { Archive, ArchiveRestore, BellOff, CheckCheck, type LucideIcon, Mail } from "lucide-react";

import { NOTIFICATION_ACTIONS, type NotificationAction } from "../constants";
import { EVENT_TYPES } from "../event-types";
import type {
  DetailField,
  NotificationData,
  NotificationFilterOptions,
  NotificationLinks,
  NotificationStatus,
} from "../types";
import { isRemoteFailureNotification } from "../utils";

import type { AuthContextType } from "@/lib/contexts/Auth/AuthContext";
import { getDateFnsLocale } from "@/utils/dateUtils";

export const SYSTEM_ACTOR_NAME = "System";

const UNKNOWN_LABEL = "Unknown";

export function resolveActorName(actorName: string | null): string {
  return actorName?.trim() || SYSTEM_ACTOR_NAME;
}

export type NotificationDateGroup = {
  dateKey: string;
  date: Date;
  notifications: NotificationData[];
};

export function groupNotificationsByDate(notifications: NotificationData[]): NotificationDateGroup[] {
  const groups: NotificationDateGroup[] = [];
  let current: NotificationDateGroup | null = null;

  for (const notification of notifications) {
    const d = new Date(notification.createdAt);
    const dateKey = format(d, "yyyy-MM-dd");

    if (!current || current.dateKey !== dateKey) {
      current = { dateKey, date: d, notifications: [] };
      groups.push(current);
    }
    current.notifications.push(notification);
  }

  return groups;
}

export function formatDateLabel(date: Date, language: string, t: (key: string) => string) {
  if (isToday(date)) return t("common.today");
  if (isYesterday(date)) return t("common.yesterday");
  return format(date, "d MMM yyyy", { locale: getDateFnsLocale(language) });
}

export function translateActorName(actorName: string, t: TFunction): string {
  if (actorName === SYSTEM_ACTOR_NAME) return t("notifications.systemActor");
  if (actorName === UNKNOWN_LABEL) return t("notifications.unknownActor");
  return actorName;
}

export function translateFilterOptions(
  options: NotificationFilterOptions,
  t: TFunction,
): NotificationFilterOptions {
  return {
    severity: options.severity.map((option) => ({
      ...option,
      label: t(`notifications.severity.${option.value}`),
    })),
    actor: options.actor.map((option) => ({
      ...option,
      label: translateActorName(option.label, t),
    })),
    client: options.client.map((option) => ({
      ...option,
      label: option.label === UNKNOWN_LABEL ? t("notifications.unknownClient") : option.label,
    })),
  };
}

type DetailFormatters = {
  t: TFunction;
  context: AuthContextType["context"];
  links: NotificationLinks;
  formatMonth: (value: string) => string;
  formatCurrency: (value: number | null | undefined) => string;
};

function formatChange(previous: string, next: string) {
  return `${previous} → ${next}`;
}

export function getChangeFields(
  notification: NotificationData,
  { t, context, links, formatMonth, formatCurrency }: DetailFormatters,
): DetailField[] {
  const { eventType, change, clientName } = notification;
  if (!change) return [];

  const sourceFields: DetailField[] = [
    ...(clientName ? [{ label: t("notifications.detail.fields.client"), value: clientName, to: links.client }] : []),
    ...("campaignName" in change
      ? [{ label: t("notifications.detail.fields.campaign"), value: change.campaignName, to: links.campaign }]
      : []),
    { label: t("notifications.detail.fields.integration"), value: change.integrationName, to: links.integration },
  ];

  if (isRemoteFailureNotification(notification)) {
    if (context !== "admin") {
      return [
        ...sourceFields,
        { label: t("notifications.detail.fields.error"), value: t("notifications.detail.clientRemoteFailure") },
      ];
    }
    const { errorMessage, errorCode } = notification.change;
    return [
      ...sourceFields,
      { label: t("notifications.detail.fields.error"), value: errorMessage },
      ...(errorCode ? [{ label: t("notifications.detail.fields.errorCode"), value: errorCode }] : []),
    ];
  }

  switch (eventType) {
  case EVENT_TYPES.campaignsUpserted:
    return [
      ...sourceFields,
      { label: t("notifications.detail.fields.month"), value: formatMonth(change.month) },
      {
        label: t("notifications.detail.fields.amount"),
        value: formatChange(formatCurrency(change.previousAmount), formatCurrency(change.amount)),
      },
    ];
  case EVENT_TYPES.integrationsCompanyRegistered:
    return [
      ...sourceFields,
      { label: t("notifications.detail.fields.contactEmail"), value: change.contactEmail },
    ];
  default:
    return sourceFields;
  }
}

export const NOTIFICATION_ACTION_PRESENTATION: Record<NotificationAction, { labelKey: string; icon: LucideIcon }> = {
  [NOTIFICATION_ACTIONS.READ]: { labelKey: "notifications.markAsRead", icon: CheckCheck },
  [NOTIFICATION_ACTIONS.UNREAD]: { labelKey: "notifications.markAsUnread", icon: Mail },
  [NOTIFICATION_ACTIONS.ARCHIVE]: { labelKey: "notifications.archive", icon: Archive },
  [NOTIFICATION_ACTIONS.UNARCHIVE]: { labelKey: "notifications.unarchive", icon: ArchiveRestore },
};

export const NOTIFICATION_EMPTY_STATES: Record<
  NotificationStatus,
  { icon: LucideIcon; title: string; description: string }
> = {
  unread: { icon: CheckCheck, title: "notifications.page.emptyUnreadTitle", description: "notifications.page.emptyUnreadDescription" },
  inbox: { icon: BellOff, title: "notifications.empty", description: "notifications.page.emptyDescription" },
  archived: { icon: Archive, title: "notifications.page.emptyArchivedTitle", description: "notifications.page.emptyArchivedDescription" },
};

const ROW_SELECTOR = "[data-notification-row]";

const ROW_FOCUS_TARGET_SELECTORS = ["a[href]", "button"] as const;

export function focusNeighbourRow(container: HTMLElement, notificationId: NotificationData["id"]) {
  const rows = [...container.querySelectorAll<HTMLElement>(ROW_SELECTOR)];
  const index = rows.findIndex((row) => row.dataset.notificationRow === notificationId);
  const neighbour = rows[index + 1] ?? rows[index - 1];
  const target = ROW_FOCUS_TARGET_SELECTORS.map((selector) => neighbour?.querySelector<HTMLElement>(selector)).find(Boolean);
  (target ?? container).focus();
}
